import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Risk {
  id: string
  title: string
  category: string
  description: string
  probability: string
  impact: string
  risk_score: number
  status: string
  owner: string
  due_date: string
}

interface Mitigation {
  id: string
  risk_title: string
  action: string
  status: string
  assigned_to: string
  effectiveness: string
  due_date: string
}

export default function RisksPage() {
  const [risks, setRisks] = useState<Risk[]>([])
  const [mitigations, setMitigations] = useState<Mitigation[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [risksRes, mitRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/risks/'),
      fetch(API_BASE + '/risks/mitigations'),
      fetch(API_BASE + '/risks/statistics')
    ])
    if (risksRes.ok) setRisks((await risksRes.json()).risks || [])
    if (mitRes.ok) setMitigations((await mitRes.json()).mitigations || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'mitigated': case 'completed': return 'bg-green-100 text-green-800'
      case 'active': case 'in_progress': return 'bg-blue-100 text-blue-800'
      case 'monitoring': case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'escalated': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getRiskColor = (score: number) => {
    if (score >= 15) return 'bg-red-100 text-red-800'
    if (score >= 10) return 'bg-orange-100 text-orange-800'
    if (score >= 5) return 'bg-yellow-100 text-yellow-800'
    return 'bg-green-100 text-green-800'
  }

  const getEffectivenessColor = (eff: string) => {
    switch (eff) {
      case 'high': return 'text-green-600'
      case 'medium': return 'text-yellow-600'
      case 'low': return 'text-red-600'
      default: return 'text-gray-600'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Risk Management</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Add Risk</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Risks</div>
              <div className="text-2xl font-bold">{statistics.total_risks}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Critical</div>
              <div className="text-2xl font-bold text-red-600">{statistics.critical_risks}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Active</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.active_risks}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Mitigated</div>
              <div className="text-2xl font-bold text-green-600">{statistics.mitigated_risks}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Avg Risk Score</div>
              <div className="text-2xl font-bold">{statistics.avg_risk_score?.toFixed(1)}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Risk Register</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Risk</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {risks.slice(0, 10).map(risk => (
                  <tr key={risk.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium">{risk.title}</div>
                      <div className="text-xs text-gray-500">{risk.owner} • Due: {risk.due_date}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm">{risk.category}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs font-bold ${getRiskColor(risk.risk_score)}`}>
                        {risk.risk_score}
                      </span>
                      <div className="text-xs text-gray-500 mt-1">
                        P: {risk.probability} / I: {risk.impact}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(risk.status)}`}>
                        {risk.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Mitigation Actions</h3>
            <div className="space-y-3">
              {mitigations.slice(0, 10).map(mit => (
                <div key={mit.id} className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-medium text-sm truncate flex-1">{mit.action}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ml-2 ${getStatusColor(mit.status)}`}>{mit.status}</span>
                  </div>
                  <div className="text-xs text-gray-500">{mit.risk_title}</div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-xs text-gray-600">{mit.assigned_to}</span>
                    <span className={`text-xs font-medium ${getEffectivenessColor(mit.effectiveness)}`}>
                      {mit.effectiveness} effectiveness
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
